import React from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as yup from 'yup'

import { messageToAccountantActions } from '@services'
import { clearDraft, loadDraft } from '@services/messageToAccountant/draft'
import type {
  MessageToAccountantPayload,
  MessageToAccountantResponse,
  MessageToAccountantTopic,
} from '@services/messageToAccountant/types'

import { bindActionToPromise, StrongTextValues } from '@helpers'

import {
  Button,
  INFORMATIONAL_MESSAGE_VARIANTS,
  ReactHookFormSelectField,
  ReactHookFormSubmitButton,
} from '@components/ui'

import {
  ActionsDiv,
  CopyButtonPositionerDiv,
  DraftPersister,
  MESSAGE_FIELD_MIN_ROWS,
  MessageCopyButton,
  MessageFieldWrapperDiv,
  StaleTopicReset,
  StyledInformationalMessage,
  StyledReactHookForm,
  StyledSlideOverBody,
  StyledTextareaField,
} from './FormElements'
import type { MessageToAccountantFormValues } from './types'

import { CancelButtonMessage, formErrorMessages } from '@messages'
import { messages } from './messages'

const INITIAL_VALUES: MessageToAccountantFormValues = {
  topic: null,
  message: '',
}

function useValidationSchema(): yup.ObjectSchema<MessageToAccountantFormValues> {
  const { formatMessage } = useIntl()

  return yup.object({
    topic: yup.number().nullable().required(formatMessage(formErrorMessages.required)),
    message: yup
      .string()
      .trim()
      .required(formatMessage(formErrorMessages.required))
      .min(1, formatMessage(formErrorMessages.minimumLength, { min: 1 })),
  })
}

interface MessageToAccountantFormProps {
  createMessage: AsyncFunction<MessageToAccountantPayload, MessageToAccountantResponse>
  isTopicsLoading: boolean
  loadTopics: VoidFunction
  onCancel: VoidFunction
  onSubmitSuccess: VoidFunction
  topics: MessageToAccountantTopic[]
  topicsFetched: boolean | undefined
  userId: Nullable<User['id']>
}

function PureMessageToAccountantForm({
  createMessage,
  isTopicsLoading,
  loadTopics,
  onCancel,
  onSubmitSuccess,
  topics,
  topicsFetched,
  userId,
}: MessageToAccountantFormProps) {
  const { formatMessage } = useIntl()
  const validationSchema = useValidationSchema()
  const [hasSubmitError, setHasSubmitError] = React.useState(false)
  const hasSentRef = React.useRef(false)
  // Restored once and kept referentially stable: ReactHookForm passes it to useForm and keys an
  // effect on its identity.
  const [initialValues] = React.useState(() =>
    userId === null ? INITIAL_VALUES : (loadDraft(userId) ?? INITIAL_VALUES)
  )

  // Mounted only while the SlideOver is open, so this loads the options on open.
  React.useEffect(() => {
    if (!topicsFetched) {
      loadTopics()
    }
  }, [loadTopics, topicsFetched])

  async function handleSubmit(values: MessageToAccountantFormValues) {
    setHasSubmitError(false)
    const response = await createMessage({
      topic: Number(values.topic),
      message: values.message,
    })
    hasSentRef.current = true
    if (userId !== null) {
      clearDraft(userId)
    }
    return response
  }

  return (
    <StyledReactHookForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onSubmitFail={() => setHasSubmitError(true)}
      onSubmitSuccess={onSubmitSuccess}
      validationSchema={validationSchema}
      skipResetAfterSuccessfulSubmit
      skipUnsavedChanges
    >
      {userId !== null && <DraftPersister hasSentRef={hasSentRef} userId={userId} />}
      {initialValues.topic !== null && <StaleTopicReset topics={topics} topicsFetched={topicsFetched} />}
      <StyledSlideOverBody>
        {hasSubmitError && (
          <StyledInformationalMessage variant={INFORMATIONAL_MESSAGE_VARIANTS.error}>
            <FormattedMessage {...messages.error} values={{ ...StrongTextValues, br: <br /> }} />
          </StyledInformationalMessage>
        )}
        <ReactHookFormSelectField<MessageToAccountantTopic>
          name="topic"
          label={formatMessage(messages.topicLabel)}
          placeholder={formatMessage(messages.topicPlaceholder)}
          options={topics}
          isLoading={isTopicsLoading}
          required
        />
        <MessageFieldWrapperDiv>
          <StyledTextareaField
            name="message"
            label={formatMessage(messages.messageLabel)}
            placeholder={formatMessage(messages.messagePlaceholder)}
            minRows={MESSAGE_FIELD_MIN_ROWS}
            required
          />
          <CopyButtonPositionerDiv>
            <MessageCopyButton />
          </CopyButtonPositionerDiv>
        </MessageFieldWrapperDiv>
      </StyledSlideOverBody>
      <ActionsDiv>
        <Button type="button" variant="secondaryText" onClick={onCancel}>
          {CancelButtonMessage}
        </Button>
        <ReactHookFormSubmitButton isCreateOnly buttonText={formatMessage(messages.submitButton)} />
      </ActionsDiv>
    </StyledReactHookForm>
  )
}

export const MessageToAccountantForm = connect(
  (state: Store) => ({
    isTopicsLoading: state.messageToAccountant.topics.loading,
    topics: state.messageToAccountant.topics.data,
    topicsFetched: state.messageToAccountant.topics.fetched,
    userId: state.auth.user?.id ?? null,
  }),
  dispatch => ({
    createMessage: bindActionToPromise<MessageToAccountantPayload, MessageToAccountantResponse>(
      dispatch,
      messageToAccountantActions.createMessageToAccountant.request
    ),
    loadTopics: bindActionCreators(messageToAccountantActions.fetchMessageToAccountantTopics.request, dispatch),
  })
)(PureMessageToAccountantForm)

MessageToAccountantForm.displayName = 'MessageToAccountantForm'
